/**
 * Medication Logs Hook
 *
 * Tracks medication intake - when doses are taken, skipped, or missed.
 * Works with the care API medication_logs table.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import type { paths } from "../../openapi/schema";
import { useTimezone } from "../../preferences";
import {
  getMedicationLogOccurrenceKey,
  isMedicationLogInDateRange,
  serializeMedicationLogScheduledFor,
  MEDICATION_LOG_COMPATIBILITY_FETCH_LIMIT,
} from "../medication-log-date";
import { fetchMedicationLogsInRange } from "../medication-log-fetch";
import { calendarKeys, medicationLogKeys } from "../patient-cache-keys";
import { throwApiError } from "../../api/api-error";
import { createIdempotencyKey } from "../../api/idempotency";

export { medicationLogKeys };

// ============================================================================
// Types — derived from OpenAPI schema
// ============================================================================

type MedicationLogResponse =
  paths["/patients/{patientId}/medications/logs"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type MedicationLog = MedicationLogResponse;

type CreateMedicationLogBody = NonNullable<
  paths["/patients/{patientId}/medications/logs"]["post"]["requestBody"]
>["content"]["application/json"];

export type CreateMedicationLogPayload = CreateMedicationLogBody;
export type LogMedicationDoseVariables = CreateMedicationLogPayload & {
  /** Reused when the same user submission is retried after an uncertain response. */
  idempotencyKey?: string;
};

const DEV_MEDICATION_LOG_STORAGE_KEY = "__louise_dev_medication_logs_v1";
const DEV_MEDICATION_LOG_PATIENT_ID = "15640000-0000-4000-8000-000000000001";

function isDevMedicationLogFixtureEnabled(): boolean {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_patient_medication_actions",
      ) === "true"
    );
  } catch {
    return false;
  }
}

function shouldFailDevMedicationLogFixture(): boolean {
  if (!isDevMedicationLogFixtureEnabled()) return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_medication_log_failure",
      ) === "true"
    );
  } catch {
    return false;
  }
}

function shouldFailDevMedicationLogReadFixture(): boolean {
  if (!isDevMedicationLogFixtureEnabled()) return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        "dev_medication_log_read_failure",
      ) === "true"
    );
  } catch {
    return false;
  }
}

function readDevMedicationLogs(): MedicationLog[] {
  if (!isDevMedicationLogFixtureEnabled()) return [];
  try {
    const raw = window.localStorage.getItem(DEV_MEDICATION_LOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MedicationLog[]) : [];
  } catch {
    return [];
  }
}

function writeDevMedicationLogs(logs: MedicationLog[]): void {
  if (!isDevMedicationLogFixtureEnabled()) return;
  try {
    window.localStorage.setItem(
      DEV_MEDICATION_LOG_STORAGE_KEY,
      JSON.stringify(logs),
    );
  } catch {
    // The fixture is only an optional browser development aid. The real API
    // path remains authoritative when browser storage is unavailable.
  }
}

function buildDevMedicationLog(
  payload: CreateMedicationLogPayload,
): MedicationLog {
  const now = new Date().toISOString();
  const existingLogs = readDevMedicationLogs();
  const existing = existingLogs.find(
    (log) =>
      log.patientMedicationId === (payload.patientMedicationId ?? null) &&
      log.scheduledFor === (payload.scheduledFor ?? null),
  );
  const log: MedicationLog = {
    id: existing?.id ?? `dev-medication-log-${Date.now()}`,
    patientId: DEV_MEDICATION_LOG_PATIENT_ID,
    providerId: null,
    patientMedicationId: payload.patientMedicationId ?? null,
    medicationName: payload.medicationName,
    dosage: payload.dosage ?? null,
    takenAt: payload.takenAt,
    scheduledFor: payload.scheduledFor ?? null,
    status: payload.status ?? "taken",
    notes: payload.notes ?? null,
    reminderId: payload.reminderId ?? null,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    updatedBy: null,
    ownership: "patient",
    ownerId: DEV_MEDICATION_LOG_PATIENT_ID,
    sourceId: null,
    audience: [],
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  };

  writeDevMedicationLogs([
    log,
    ...existingLogs.filter((candidate) => candidate.id !== log.id),
  ]);
  return log;
}

function sameMedicationLogOccurrence(
  a: MedicationLog,
  b: MedicationLog,
  timezone?: string,
) {
  const aScheduledFor = getMedicationLogOccurrenceKey(a.scheduledFor, timezone);
  const bScheduledFor = getMedicationLogOccurrenceKey(b.scheduledFor, timezone);
  if (!aScheduledFor || !bScheduledFor || aScheduledFor !== bScheduledFor) {
    return false;
  }

  if (
    a.patientMedicationId &&
    b.patientMedicationId &&
    a.patientMedicationId === b.patientMedicationId
  ) {
    return true;
  }

  const aName = a.medicationName?.trim().toLowerCase();
  const bName = b.medicationName?.trim().toLowerCase();
  return Boolean(aName && bName && aName === bName);
}

// ============================================================================
// Hooks
// ============================================================================

export interface UseMedicationLogsOptions {
  startDate?: string;
  endDate?: string;
  /** Patient-local IANA timezone used for occurrence date boundaries. */
  timezone?: string;
  limit?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch medication logs.
 *
 * With both startDate and endDate, the occurrence-aware server contract
 * (dashboard-api#664) is queried in padded, cursor-paginated windows and the
 * exact patient-local occurrence filter is applied on top. Without a full
 * range, a single capped page is fetched and filtered client-side.
 */
export function useMedicationLogs(options: UseMedicationLogsOptions = {}) {
  const { startDate, endDate, timezone, limit, enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();
  const preferredTimezone = useTimezone();
  const scheduleTimezone = timezone ?? preferredTimezone;
  const devFixtureEnabled = isDevMedicationLogFixtureEnabled();
  const queryPatientId =
    patientId ?? (devFixtureEnabled ? DEV_MEDICATION_LOG_PATIENT_ID : "");

  return useQuery({
    queryKey: medicationLogKeys.list(
      tenantId ?? "",
      queryPatientId,
      startDate,
      endDate,
      limit ?? MEDICATION_LOG_COMPATIBILITY_FETCH_LIMIT,
      scheduleTimezone,
    ),
    queryFn: async () => {
      if (devFixtureEnabled) {
        if (shouldFailDevMedicationLogReadFixture()) {
          throw new Error("The medication log fixture rejected this read");
        }
        let logs = readDevMedicationLogs();
        if (startDate || endDate) {
          logs = logs.filter((log) =>
            isMedicationLogInDateRange(log, {
              startDate,
              endDate,
              timezone: scheduleTimezone,
            }),
          );
        }
        return limit === undefined ? logs : logs.slice(0, limit);
      }

      if (!tenantId || !patientId) return [];

      try {
        const result = await fetchMedicationLogsInRange(client, patientId, {
          startDate,
          endDate,
          timezone: scheduleTimezone,
        });
        if (result.data === undefined) {
          throwApiError(
            result.error,
            "Failed to load medication logs",
            "medication-logs",
            result.status,
          );
        }

        let logs = result.data;

        // The server windows are padded, and legacy rows without scheduledFor
        // can carry mixed timestamp semantics — this exact patient-local
        // occurrence filter stays authoritative.
        if (startDate || endDate) {
          logs = logs.filter((log) =>
            isMedicationLogInDateRange(log, {
              startDate,
              endDate,
              timezone: scheduleTimezone,
            }),
          );
        }

        if (limit !== undefined) {
          logs = logs.slice(0, limit);
        }

        return logs;
      } catch (error) {
        throwApiError(
          error,
          "Failed to load medication logs",
          "medication-logs",
          undefined,
        );
      }
    },
    enabled: enabled && !!tenantId && (!!patientId || devFixtureEnabled),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to log a medication dose (taken or skipped).
 */
export function useLogMedicationDose() {
  const { client, tenantId, patientId } = useApiClient();
  const scheduleTimezone = useTimezone();
  const queryClient = useQueryClient();
  const devFixtureEnabled = isDevMedicationLogFixtureEnabled();
  const queryPatientId =
    patientId ?? (devFixtureEnabled ? DEV_MEDICATION_LOG_PATIENT_ID : "");
  const patientLogKey = medicationLogKeys.patient(
    tenantId ?? "",
    queryPatientId,
  );
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    queryPatientId,
  );

  return useMutation({
    mutationFn: async ({
      idempotencyKey,
      ...payload
    }: LogMedicationDoseVariables) => {
      if (devFixtureEnabled) {
        if (shouldFailDevMedicationLogFixture()) {
          throw new Error("The medication log fixture rejected this save");
        }
        return buildDevMedicationLog(payload);
      }

      if (!tenantId || !patientId) {
        throw new Error("No tenant ID");
      }

      const response = await client.POST(
        "/patients/{patientId}/medications/logs",
        {
          params: { path: { patientId: patientId! } },
          headers: {
            "Idempotency-Key": idempotencyKey ?? createIdempotencyKey(),
          },
          body: {
            ...payload,
            // Scheduling surfaces pass scheduledFor as patient-local wall
            // clock; the API only accepts explicit ISO instants (#2357).
            scheduledFor:
              serializeMedicationLogScheduledFor(
                payload.scheduledFor,
                scheduleTimezone,
              ) ?? undefined,
          },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to log medication",
          "useLogMedicationDose",
          response.response.status,
        );
      }

      return response.data.data;
    },
    onMutate: async (payload) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: patientLogKey });
      await queryClient.cancelQueries({ queryKey: patientCalendarKey });

      // Snapshot previous data
      const previousLogs = queryClient.getQueriesData<MedicationLog[]>({
        queryKey: patientLogKey,
      });

      // Build optimistic log entry
      const tempId = `temp-log-${Date.now()}`;
      const optimisticLog: MedicationLog = {
        id: tempId,
        patientId: "",
        providerId: null,
        patientMedicationId: payload.patientMedicationId ?? null,
        medicationName: payload.medicationName,
        dosage: payload.dosage ?? null,
        takenAt: payload.takenAt,
        scheduledFor: payload.scheduledFor ?? null,
        status: payload.status ?? "taken",
        notes: payload.notes ?? null,
        reminderId: payload.reminderId ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        updatedBy: null,
        ownership: "patient",
        ownerId: "",
        sourceId: null,
        audience: [],
        source: "patient",
        verificationStatus: "unverified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      };

      // Optimistically add to medication logs
      queryClient.setQueriesData<MedicationLog[]>(
        { queryKey: patientLogKey },
        (old) => {
          if (!old) return [optimisticLog];
          return [optimisticLog, ...old];
        },
      );

      return { previousLogs, optimisticLogId: tempId };
    },
    onSuccess: (serverLog, _payload, context) => {
      queryClient.setQueriesData<MedicationLog[]>(
        { queryKey: patientLogKey },
        (old) => {
          if (!old) return [serverLog];

          let inserted = false;
          const reconciled = old.reduce<MedicationLog[]>((next, log) => {
            if (
              log.id === context?.optimisticLogId ||
              log.id === serverLog.id ||
              sameMedicationLogOccurrence(log, serverLog, scheduleTimezone)
            ) {
              if (!inserted) {
                next.push(serverLog);
                inserted = true;
              }
              return next;
            }

            next.push(log);
            return next;
          }, []);

          return inserted ? reconciled : [serverLog, ...reconciled];
        },
      );
    },
    onError: (error, _payload, context) => {
      console.error("[useLogMedicationDose] API error:", error);
      // Rollback on error
      if (context?.previousLogs) {
        for (const [queryKey, data] of context.previousLogs) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      // Keep the server response in the active cache without immediately
      // refetching the legacy GET endpoint. Marking the log query stale means
      // a subsequent reload/focus fetches the durable server record while the
      // current row keeps its reconciled status.
      queryClient.invalidateQueries({
        queryKey: patientLogKey,
        refetchType: "none",
      });
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
    },
  });
}

/**
 * Convenience hook to mark a medication as taken.
 */
export function useMarkMedicationTaken() {
  const logDose = useLogMedicationDose();

  return useMutation({
    mutationFn: async ({
      patientMedicationId,
      medicationName,
      dosage,
      scheduledFor,
      reminderId,
      notes,
      status = "taken",
    }: {
      patientMedicationId?: string;
      medicationName: string;
      dosage?: string;
      scheduledFor?: string;
      reminderId?: string;
      notes?: string;
      status?: "taken" | "late";
    }) => {
      return logDose.mutateAsync({
        patientMedicationId,
        medicationName,
        dosage,
        takenAt: new Date().toISOString(),
        scheduledFor,
        status,
        reminderId,
        notes,
      });
    },
  });
}

/**
 * Convenience hook to mark a medication as skipped.
 */
export function useMarkMedicationSkipped() {
  const logDose = useLogMedicationDose();

  return useMutation({
    mutationFn: async ({
      patientMedicationId,
      medicationName,
      dosage,
      scheduledFor,
      reminderId,
    }: {
      patientMedicationId?: string;
      medicationName: string;
      dosage?: string;
      scheduledFor?: string;
      reminderId?: string;
    }) => {
      return logDose.mutateAsync({
        patientMedicationId,
        medicationName,
        dosage,
        takenAt: new Date().toISOString(),
        scheduledFor,
        status: "skipped",
        reminderId,
      });
    },
  });
}
